import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../ThemeContext";
import {
  handleNotificationAction,
  formatNotificationTime,
  getModuleBadgeColor,
} from "../../utils/notificationNavigation";

const ORANGE = "#ffbe63";

export default function NotificationItem({ notification, onMarkRead, onItemClick }) {
  const { theme } = useTheme();
  const navigate = useNavigate();

  if (!notification) return null;

  const isDark = theme === "dark";
  const isUnread = !notification.is_read;

  const handleClick = (e) => {
    e.stopPropagation();

    // 1. If unread, mark read using UserNotification.id
    if (isUnread && onMarkRead) {
      onMarkRead(notification.id);
    }

    // 2. Close dropdown if callback passed
    if (onItemClick) {
      onItemClick(notification);
    }

    // 3. Navigate to destination route
    handleNotificationAction(notification, navigate);
  };

  return (
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      className={`p-3 border-b transition-colors cursor-pointer text-left flex items-start gap-3 relative ${
        isDark
          ? isUnread
            ? "bg-[#282834] hover:bg-[#30303e] border-gray-800"
            : "bg-[#1e1e26] hover:bg-[#252530] border-gray-800/60"
          : isUnread
          ? "bg-[#fff8f0] hover:bg-[#fff2e0] border-gray-100"
          : "bg-white hover:bg-gray-50 border-gray-100"
      }`}
    >
      {/* Unread Accent Indicator */}
      {isUnread && (
        <span
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l"
          style={{ background: ORANGE }}
        />
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 min-w-0">
            {notification.module && (
              <span
                className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border ${getModuleBadgeColor(
                  notification.module
                )}`}
              >
                {notification.module}
              </span>
            )}
            <h4
              className={`text-xs sm:text-sm font-semibold truncate ${
                isDark ? "text-gray-100" : "text-gray-900"
              }`}
            >
              {notification.title || "Notification"}
            </h4>
          </div>
          <span className="text-[10px] text-gray-400 whitespace-nowrap">
            {formatNotificationTime(notification.created_at)}
          </span>
        </div>

        {notification.body && (
          <p
            className={`text-xs line-clamp-2 ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {notification.body}
          </p>
        )}

        {notification.actor_name && (
          <span className="text-[10px] text-gray-400 mt-1 block">
            By {notification.actor_name}
          </span>
        )}
      </div>

      {isUnread && (
        <div
          className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
          style={{ background: ORANGE }}
          title="Unread"
        />
      )}
    </div>
  );
}
